import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	OverrideContainerComponent,
	Store,
} from 'react-sweet-state';
import {
	createStore,
	createHook,
	createActionsHook,
	createContainer,
	createStateHook,
} from 'react-sweet-state';
type SideNavigationCollapsedState = {
	isCollapsed: boolean;
};
const initialState: SideNavigationCollapsedState = {
	isCollapsed: false,
};
const actions = {
	setIsCollapsed:
		(collapsed: boolean) =>
		({ setState }: any): void => {
			setState({ isCollapsed: collapsed });
		},
};
export const SideNavigationCollapsedStore: Store<
	SideNavigationCollapsedState,
	{
		setIsCollapsed: (collapsed: boolean) => ({ setState }: any) => void;
	}
> = createStore({
	initialState,
	actions,
});

export const useSideNavigationCollapsedState: HookFunction<
	SideNavigationCollapsedState,
	BoundActions<
		SideNavigationCollapsedState,
		{
			setIsCollapsed: (collapsed: boolean) => ({ setState }: any) => void;
		}
	>,
	void
> = createHook(SideNavigationCollapsedStore);

export const useSideNavigationCollapsedActions: HookActionsFunction<
	BoundActions<
		SideNavigationCollapsedState,
		{
			setIsCollapsed: (collapsed: boolean) => ({ setState }: any) => void;
		}
	>
> = createActionsHook(SideNavigationCollapsedStore);

/**
 * Whether the side navigation is collapsed. Selector-based, so consumers only re-render when this
 * value changes rather than on any store update.
 */
export const useIsSideNavCollapsed: HookStateFunction<boolean, void> = createStateHook(
	SideNavigationCollapsedStore,
	{ selector: (state: SideNavigationCollapsedState) => state.isCollapsed },
);

type SideNavigationCollapsedContainerProps = {
	/**
	 * The collapsed state the side nav was actually rendered with, i.e.
	 * `useSideNavInitialState().initialCollapsedState === 'collapsed'`.
	 *
	 * MUST come from `useSideNavInitialState`, not straight from localStorage: that hook also applies
	 * the Recap override, the stable-UI test override, and the anonymous-user SSR parity branch.
	 * Seeding with the same value the DOM was rendered from is what keeps consumers free of hydration
	 * mismatches, and what keeps this seed from fighting `needsPostHydrationCollapse`.
	 */
	initialIsCollapsed: boolean;
};

/**
 * Seeds `SideNavigationCollapsedStore` from the user's persisted preference synchronously, during
 * render, before any consumer reads the store — so the store is correct on first render rather than
 * one frame late.
 *
 * A seed is required, not just nice to have: `@atlaskit/navigation-system` never fires a mount-time
 * `onExpand`/`onCollapse` (its visibility-callbacks hook returns early until it has a previous state
 * to compare against), so without this the store would stay at `initialState` until the user's first
 * real toggle.
 *
 * ⚠️ MUST be rendered with `isGlobal`. Without `scope` or `isGlobal`, react-sweet-state gives the
 * container its own local StoreRegistry, so product code outside this container's subtree would
 * silently read a *different*, never-seeded store instance. `isGlobal` makes the container seed the
 * global instance that `useIsSideNavCollapsed` resolves everywhere.
 *
 * Renders no DOM, so it can be mounted childless as a bootstrap container without re-parenting the
 * layout tree.
 *
 * `onInit` re-runs on each mount, which is intentional and idempotent: `SideNavigation`'s
 * onCollapse/onExpand mirror every toggle to the same localStorage key that `useSideNavInitialState`
 * reads, so re-seeding always re-applies the current value.
 *
 * This is also the first server-side write to this store, and react-sweet-state's default registry is
 * a module singleton that outlives a single SSR request. That is safe because this container renders
 * before any consumer on every request, and the read hooks use `useSyncExternalStore` with the same
 * snapshot on the server — so every reader within a request sees post-seed state.
 */
export const SideNavigationCollapsedContainer: OverrideContainerComponent<SideNavigationCollapsedContainerProps> =
	createContainer<
		SideNavigationCollapsedState,
		typeof actions,
		SideNavigationCollapsedContainerProps
	>(SideNavigationCollapsedStore, {
		onInit:
			() =>
			({ setState }, { initialIsCollapsed }) => {
				setState({ isCollapsed: initialIsCollapsed });
			},
	});
